import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import Loader from "react-loader-spinner";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { Container } from "../../components/styles/Layout";
import { LoaderWrapper } from "../../components/styles/Loader";

import useSeo from "../../hooks/useSeo";
import useJsonLd from "../../hooks/useJsonLd";
import { housePricesSeo } from "../../data/seo";
import { housePricesSchema } from "../../data/structuredData";
import NotFound from "./NotFound";

/*
 * House prices for one postcode district.
 *
 * WHY THIS PAGE EXISTS, since the report tool already shows sold prices: the
 * tool cannot be found. Its results never reach the URL, so there is one route
 * and nothing at a crawlable address for a search engine to read. "House
 * prices in SW17" is a real search with a real answer, and we hold a better
 * answer than most sites that rank for it - every sale since 1995 rather than
 * a sample.
 *
 * IT MUST NOT BE A DOORWAY PAGE. A set of near-identical pages whose only job
 * is to funnel people somewhere else is spam, and is treated as spam. Every
 * number here is computed for this district: the count, the median and
 * quartiles, the median by property type, ten years of medians, and the most
 * recent sales with addresses. The page answers its own question; the link to
 * the report is a next step, not the reason it exists. If this ever thins out
 * to a heading and a button, delete it rather than keep it.
 *
 * The server renders the same content at api/render.js for crawlers and for
 * anyone whose browser never runs this file. Keep the two saying the same
 * thing - a served page that differs from the rendered one is the mismatch
 * both of them exist to avoid.
 */

const Header = styled.div`
    background: ${(props) => props.theme.darkgradient};
    color: white;
    padding: 3rem 0;
    margin-bottom: 2.5rem;
`;
const Title = styled.h1`
    margin: 0 0 0.75rem 0;
    color: white;
    font-size: 2rem;
    font-weight: 400;
    letter-spacing: 1px;
`;
const Lede = styled.p`
    margin: 0;
    max-width: 46rem;
    color: white;
    font-size: 1rem;
    line-height: 1.6;
`;

const BOX_BORDER = "1px solid #e6e9ee";
const BOX_RADIUS = "6px";
const BOX_SHADOW = "0 2px 4px rgba(20, 49, 91, 0.06), 0 10px 24px rgba(20, 49, 91, 0.1)";

const Panel = styled.section`
    border: ${BOX_BORDER};
    border-radius: ${BOX_RADIUS};
    box-shadow: ${BOX_SHADOW};
    background: #fff;
    margin-bottom: 1.5rem;
    overflow: hidden;
`;
const PanelHead = styled.header`
    padding: 1rem 1.25rem;
    border-bottom: ${BOX_BORDER};
    h2 {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 600;
        color: ${(props) => props.theme.darkblue};
    }
`;
const PanelBody = styled.div`
    padding: 1.1rem 1.25rem 1.35rem;
`;

/* The four headline numbers. Same shape as the report's fact grid. */
const Facts = styled.dl`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(150px, 100%), 1fr));
    gap: 1rem 1.5rem;
    margin: 0;
    dt {
        font-size: 0.7rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        font-weight: 600;
        color: ${(props) => props.theme.grey};
        margin-bottom: 0.2rem;
    }
    dd {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: ${(props) => props.theme.darkblue};
        font-variant-numeric: tabular-nums;
    }
`;

const Rows = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;
const Row = styled.li`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.15rem 1rem;
    padding: 0.6rem 0;
    border-bottom: 1px solid #f3f3f3;
    &:last-child {
        border-bottom: 0;
    }
`;
const RowName = styled.span`
    font-weight: 600;
    overflow-wrap: anywhere;
`;
const RowValue = styled.span`
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    font-weight: 600;
`;
const RowDetail = styled.span`
    grid-column: 1 / -1;
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
`;

/*
 * The ten-year trend as bars rather than a chart library. Each bar is sized
 * against the highest median in the run, so the shape is the comparison and
 * the figure beside it is the fact. No dependency, prints correctly, and
 * readable at phone width - a canvas chart would be none of those.
 */
const Trend = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;
const TrendRow = styled.li`
    display: grid;
    grid-template-columns: 3.4rem 1fr auto;
    align-items: center;
    gap: 0.6rem;
    padding: 0.28rem 0;
    font-variant-numeric: tabular-nums;
`;
const TrendYear = styled.span`
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
`;
const TrendBar = styled.span`
    display: block;
    height: 0.75rem;
    border-radius: 2px;
    background: ${(props) => props.theme.darkblue};
    opacity: ${(props) => (props.partial ? 0.45 : 1)};
    width: ${(props) => props.pct}%;
    min-width: 2px;
`;
const TrendValue = styled.span`
    font-size: 0.85rem;
    font-weight: 600;
    white-space: nowrap;
`;

const Note = styled.p`
    margin: 1rem 0 0;
    font-size: 0.82rem;
    line-height: 1.55;
    color: ${(props) => props.theme.grey};
    max-width: 46rem;
`;
const Actions = styled.p`
    margin: 1.5rem 0 3rem;
    font-size: 0.95rem;
    a {
        font-weight: 600;
    }
`;

/*
 * The district-against-the-country panel.
 *
 * Deliberately NOT colour-coded good/bad. "Risen faster" is excellent news
 * for a seller and bad news for the same person as a buyer, and the page
 * does not know which they are. The verdict carries weight through type,
 * and the arrows carry direction; neither claims a verdict is desirable.
 */
const Verdict = styled.p`
    margin: 0 0 0.9rem;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.45;
    color: ${(props) => props.theme.darkblue};
`;
const Compare = styled.dl`
    margin: 0;
    display: grid;
    gap: 0.55rem;
`;
const CompareRow = styled.div`
    display: grid;
    grid-template-columns: minmax(7rem, 1fr) auto auto;
    align-items: baseline;
    gap: 0.35rem 0.9rem;
    padding-bottom: 0.55rem;
    border-bottom: 1px solid #f3f3f3;
    &:last-child {
        border-bottom: 0;
        padding-bottom: 0;
    }
    /* At phone width three columns will not fit honestly, so the figures
       drop under the label rather than being squeezed into ellipsis. */
    @media (max-width: 29rem) {
        grid-template-columns: 1fr auto;
    }
`;
const CompareLabel = styled.dt`
    font-weight: 600;
    color: ${(props) => props.theme.darkblue};
    span {
        display: block;
        font-weight: 400;
        font-size: 0.78rem;
        color: ${(props) => props.theme.grey};
        font-variant-numeric: tabular-nums;
    }
`;
const CompareMove = styled.dd`
    margin: 0;
    font-size: 0.9rem;
    color: ${(props) => props.theme.grey};
    font-variant-numeric: tabular-nums;
    @media (max-width: 29rem) {
        grid-column: 1;
    }
`;
const ComparePct = styled.dd`
    margin: 0;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.theme.darkblue};
    &::before {
        content: "${(props) => (props.up ? "\\2191" : "\\2193")}";
        margin-right: 0.25rem;
        font-weight: 400;
    }
`;

/*
 * "House prices near X" - the same lateral links the renderer emits.
 *
 * Wraps rather than scrolls, and the chips are sized so a thumb can hit one
 * at phone width; a dozen short links in a row is the one layout that does
 * not need a grid.
 */
const Nearby = styled.nav`
    margin: 1.5rem 0 0;
    ul {
        list-style: none;
        margin: 0.6rem 0 0;
        padding: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
    }
    li a {
        display: block;
        padding: 0.45rem 0.8rem;
        border: 1px solid ${(props) => props.theme.lightgrey};
        border-radius: ${(props) => props.theme.radius};
        font-size: 0.9rem;
        font-weight: 600;
        text-decoration: none;
        color: ${(props) => props.theme.darkblue};
        background: #fff;
        &:hover,
        &:focus-visible {
            border-color: ${(props) => props.theme.darkblue};
            background: #f4f8fc;
        }
    }
`;
const NearbyHead = styled.h2`
    margin: 0;
    font-size: 1.02rem;
    font-weight: 600;
    color: ${(props) => props.theme.darkblue};
`;

function gbp(n) {
    if (n === null || n === undefined) return "—";
    return "£" + Math.round(Number(n)).toLocaleString("en-GB");
}
function num(n) {
    return Number(n).toLocaleString("en-GB");
}
/* A signed percentage, with the trailing ".0" dropped: "+34%", "-5.2%". */
function pct(n) {
    if (n === null || n === undefined) return "—";
    return (n > 0 ? "+" : "") + Number(n).toFixed(1).replace(/\.0$/, "") + "%";
}

export default function HousePrices() {
    const { district } = useParams();
    const [stats, setStats] = useState(null);
    const [state, setState] = useState("loading");

    useEffect(() => {
        let live = true;
        setState("loading");
        axios
            .get(process.env.REACT_APP_API_URL + "property-data/house-prices/" + encodeURIComponent(district))
            .then((res) => {
                if (!live) return;
                setStats(res.data);
                setState("ready");
            })
            .catch(() => {
                if (!live) return;
                setState("missing");
            });
        return () => {
            live = false;
        };
    }, [district]);

    const code = stats ? stats.district : String(district || "").toUpperCase();
    useSeo(housePricesSeo(code, stats));
    useJsonLd(stats ? housePricesSchema(code, stats) : null);

    if (state === "missing") return <NotFound />;

    if (state === "loading") {
        return (
            <>
                <SiteNav />
                <LoaderWrapper>
                    <Loader type="TailSpin" color="#14315B" height={60} width={60} />
                </LoaderWrapper>
                <SiteFooter />
            </>
        );
    }

    const peak = stats.byYear.reduce((m, y) => (y.median > m ? y.median : m), 0);

    return (
        <>
            <SiteNav />
            <Header>
                <Container>
                    <Title>House prices in {stats.district}</Title>
                    <Lede>
                        Over the last {stats.years} years, {num(stats.total)} homes sold in {stats.district}
                        {stats.authority ? ", mostly in " + stats.authority : ""}. The median was{" "}
                        {gbp(stats.median)}, with the middle half of sales between {gbp(stats.lower)} and{" "}
                        {gbp(stats.upper)}.
                    </Lede>
                </Container>
            </Header>

            <Container>
                <Panel>
                    <PanelHead>
                        <h2>The last {stats.years} years</h2>
                    </PanelHead>
                    <PanelBody>
                        <Facts>
                            <div>
                                <dt>Median</dt>
                                <dd>{gbp(stats.median)}</dd>
                            </div>
                            <div>
                                <dt>Middle half</dt>
                                <dd>
                                    {gbp(stats.lower)} &ndash; {gbp(stats.upper)}
                                </dd>
                            </div>
                            <div>
                                <dt>Sales</dt>
                                <dd>{num(stats.total)}</dd>
                            </div>
                            <div>
                                <dt>Full range</dt>
                                <dd>
                                    {gbp(stats.lowest)} &ndash; {gbp(stats.highest)}
                                </dd>
                            </div>
                        </Facts>
                        <Note>
                            {num(stats.allTime)} sales are recorded in {stats.district} in total
                            {stats.from ? ", going back to " + stats.from : ""}.
                        </Note>
                    </PanelBody>
                </Panel>

                {stats.byType.length > 0 && (
                    <Panel>
                        <PanelHead>
                            <h2>Median price by property type</h2>
                        </PanelHead>
                        <PanelBody>
                            <Rows>
                                {stats.byType.map((t) => (
                                    <Row key={t.type}>
                                        <RowName>{t.label}</RowName>
                                        <RowValue>{gbp(t.median)}</RowValue>
                                        <RowDetail>{num(t.count)} sales</RowDetail>
                                    </Row>
                                ))}
                            </Rows>
                        </PanelBody>
                    </Panel>
                )}

                {stats.byYear.length > 0 && (
                    <Panel>
                        <PanelHead>
                            <h2>Median price by year</h2>
                        </PanelHead>
                        <PanelBody>
                            <Trend>
                                {stats.byYear.map((y) => (
                                    <TrendRow key={y.year}>
                                        <TrendYear>{y.year}</TrendYear>
                                        <TrendBar
                                            pct={peak ? Math.round((y.median / peak) * 100) : 0}
                                            partial={y.year === stats.currentYear}
                                        />
                                        <TrendValue>{gbp(y.median)}</TrendValue>
                                    </TrendRow>
                                ))}
                            </Trend>
                            <Note>
                                {stats.currentYear} is the year so far and is shown paler, because it is not a
                                whole year and the last two months of any Price Paid release are incomplete.
                            </Note>
                        </PanelBody>
                    </Panel>
                )}

                {/*
                  * The comparison the rest of the page implies but never
                  * states. A district up a third in a decade is good or bad
                  * news entirely depending on what everywhere else did, and
                  * a local median alone cannot tell a seller which.
                  *
                  * Absent rather than empty when there is no honest answer -
                  * too few sales in an endpoint year, or no national series
                  * computed yet. See nationalPrices.compare.
                  */}
                {stats.vsNational && (
                    <Panel>
                        <PanelHead>
                            <h2>{stats.district} against England &amp; Wales</h2>
                        </PanelHead>
                        <PanelBody>
                            <Verdict verdict={stats.vsNational.verdict}>
                                {stats.vsNational.verdict === "inline"
                                    ? stats.district + " has moved broadly in line with England & Wales."
                                    : stats.vsNational.verdict === "faster"
                                    ? stats.district + " has risen faster than England & Wales."
                                    : stats.district + " has risen more slowly than England & Wales."}
                            </Verdict>
                            <Compare>
                                <CompareRow>
                                    <CompareLabel>
                                        {stats.district}
                                        <span>
                                            {stats.vsNational.from}–{stats.vsNational.to}
                                        </span>
                                    </CompareLabel>
                                    <CompareMove>
                                        {gbp(stats.vsNational.district.start)} &rarr;{" "}
                                        {gbp(stats.vsNational.district.end)}
                                    </CompareMove>
                                    <ComparePct up={stats.vsNational.district.changePct >= 0}>
                                        {pct(stats.vsNational.district.changePct)}
                                    </ComparePct>
                                </CompareRow>
                                <CompareRow>
                                    <CompareLabel>
                                        England &amp; Wales
                                        <span>
                                            {stats.vsNational.from}–{stats.vsNational.to}
                                        </span>
                                    </CompareLabel>
                                    <CompareMove>
                                        {gbp(stats.vsNational.national.start)} &rarr;{" "}
                                        {gbp(stats.vsNational.national.end)}
                                    </CompareMove>
                                    <ComparePct up={stats.vsNational.national.changePct >= 0}>
                                        {pct(stats.vsNational.national.changePct)}
                                    </ComparePct>
                                </CompareRow>
                            </Compare>
                            <Note>
                                Both are median sold prices from the same Land Registry data, over the same
                                complete calendar years; {stats.currentYear} is left out of both because it is
                                not finished. A median also moves when the mix of what sold moves, so treat a
                                difference of a few points as noise rather than as a trend.
                            </Note>
                        </PanelBody>
                    </Panel>
                )}

                {stats.recent.length > 0 && (
                    <Panel>
                        <PanelHead>
                            <h2>Most recent sales</h2>
                        </PanelHead>
                        <PanelBody>
                            <Rows>
                                {stats.recent.map((r, i) => (
                                    <Row key={r.address + r.date + i}>
                                        <RowName>{r.address || r.postcode}</RowName>
                                        <RowValue>{gbp(r.price)}</RowValue>
                                        <RowDetail>
                                            {r.postcode}
                                            {" · "}
                                            {new Date(r.date).toLocaleDateString("en-GB", {
                                                day: "numeric",
                                                month: "short",
                                                year: "numeric",
                                            })}
                                            {r.typeLabel ? " · " + r.typeLabel : ""}
                                        </RowDetail>
                                    </Row>
                                ))}
                            </Rows>
                        </PanelBody>
                    </Panel>
                )}

                <Note>
                    Figures are computed from HM Land Registry Price Paid data, which records sales lodged for
                    registration in England and Wales. It excludes transfers that were not at open-market value,
                    and the most recent two months are incomplete. Repossessions and portfolio transfers are left
                    out of these medians. Contains HM Land Registry data &copy; Crown copyright and database right{" "}
                    {new Date().getFullYear()}, licensed under the Open Government Licence v3.0.
                </Note>

                {/*
                  * The same links the renderer puts in the served HTML. They
                  * are there for crawling - a district page used to be a dead
                  * end, which is why 1.55k of them had never been fetched -
                  * but they are here because somebody comparing one district
                  * with the next one over is the normal case, not a rare one.
                  */}
                {stats.neighbours && stats.neighbours.length > 0 && (
                    <Nearby aria-labelledby="nearby-districts">
                        <NearbyHead id="nearby-districts">
                            House prices near {stats.district}
                        </NearbyHead>
                        <ul>
                            {stats.neighbours.map((n) => (
                                <li key={n}>
                                    <Link to={"/house-prices/" + n.toLowerCase()}>{n}</Link>
                                </li>
                            ))}
                        </ul>
                    </Nearby>
                )}

                <Actions>
                    <Link to="/property-report">
                        Get a full free report for one address in {stats.district}
                    </Link>
                    {" · "}
                    <Link to="/house-prices">House prices in other districts</Link>
                </Actions>
            </Container>
            <SiteFooter />
        </>
    );
}
